/**
 * 個人の gb74 scores → 組織集約用の個人サブスコア導出 (実データ版組織レポート用)
 *
 * docs/mockups/個人回答_組織スコア集約ロジック設計.md / GBbrain_最終確定設問一覧.md の確定仕様に基づき、
 * 1人分の gb74 scores から「働きがい」「つながり5変数」「離職の兆し」を算出する。
 * これを会社の全メンバー分集めて orgScore.ts の集約関数に渡す。
 *
 *  - 働きがい(wellbeing): Big5 6ファセット(遂行力/秩序性/感情制御/主導性/配慮性/挑戦性)の平均 1.0〜5.0
 *  - つながり5変数(connVars): ONA O-01〜O-10 を逆転処理して算出 (各 1.0〜5.0、高い=良い)
 *  - 離職の兆し(retention): R-01〜R-03 の平均 1.0〜5.0 (高い=良い)
 *  - 軸(axis): thinkingType A/B/C/D の個数 (0〜12)
 */
import {
  getAxisScores,
  getFactorScore,
  BIG5_FACTORS,
  type Gb74Scores,
  type AxisKey,
} from './gb74Report';
import type { ConnectionVars } from './orgScore';

/** 働きがいを構成する Big5 6ファセットのキー (設計書準拠) */
const WELLBEING_FACET_KEYS = [
  'execution', // 遂行力
  'order', // 秩序性
  'emotionControl', // 感情制御
  'initiative', // 主導性
  'consideration', // 配慮性
  'challenge', // 挑戦性
] as const;

/** 1人分の組織集約用サブスコア */
export interface IndividualOrgFactors {
  axis: Record<AxisKey, number>;
  /** 働きがい個人スコア 1.0〜5.0 (6ファセット平均) */
  wellbeing: number;
  /** つながり5変数 (各1.0〜5.0、逆転処理済み) */
  connVars: ConnectionVars;
  /** 離職の兆し個人スコア 1.0〜5.0 */
  retention: number;
}

/** 5段階の逆転値 (6 - value)。null/0は対象外 */
function reverse5(v: number | undefined): number | null {
  if (v == null) return null;
  return 6 - v;
}

/** 2項目の平均 (欠損は除外、両方欠損は null) */
function avgOf(vals: Array<number | null>): number | null {
  const xs = vals.filter((v): v is number => v != null);
  if (xs.length === 0) return null;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

/** ONA 5変数を導出 (O-03/O-05/O-07 は逆転)。欠損変数は 3.0(中立) で補完 */
function deriveConnectionVars(ona: Record<string, number>): ConnectionVars {
  const o = (code: string) => ona?.[code];
  const fallback = (v: number | null) => (v == null ? 3.0 : v);
  return {
    help: fallback(avgOf([o('O-01') ?? null, o('O-02') ?? null])), // 三角接続(正)
    vdep: fallback(avgOf([reverse5(o('O-03')), o('O-04') ?? null])), // V字依存(O-03逆転)
    boss: fallback(avgOf([reverse5(o('O-05')), o('O-06') ?? null])), // 社長集中(O-05逆転)
    silo: fallback(avgOf([reverse5(o('O-07')), o('O-08') ?? null])), // 属人化(O-07逆転)
    bridge: fallback(avgOf([o('O-09') ?? null, o('O-10') ?? null])), // 橋渡し経路(正)
  };
}

/** 働きがい(6ファセット平均) を導出。欠損ファセットは除外 */
function deriveWellbeing(scores: Gb74Scores): number {
  const facetScores = WELLBEING_FACET_KEYS.map((key) => {
    const factor = BIG5_FACTORS.find((f) => f.key === key);
    return factor ? getFactorScore(scores, factor) : 0;
  }).filter((v) => v > 0);
  if (facetScores.length === 0) return 3.0;
  return facetScores.reduce((a, b) => a + b, 0) / facetScores.length;
}

/** 離職の兆し(R-01〜03平均) を導出 */
function deriveRetention(retention: Record<string, number>): number {
  const vals = ['R-01', 'R-02', 'R-03']
    .map((c) => retention?.[c])
    .filter((v): v is number => v != null);
  if (vals.length === 0) return 3.0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/**
 * 1人分の gb74 scores から組織集約用サブスコアを導出する。
 */
export function deriveIndividualFactors(scores: Gb74Scores): IndividualOrgFactors {
  return {
    axis: getAxisScores(scores),
    wellbeing: deriveWellbeing(scores),
    connVars: deriveConnectionVars(scores.ona ?? {}),
    retention: deriveRetention(scores.retention ?? {}),
  };
}
